import { useState, useEffect } from "react";
import API, { listMyPromoPurchases, getUpgradeEligibility } from "../api/api";

function parseNumericLevel(val) {
  if (val === null || val === undefined) return 0;
  if (typeof val === "number") return isNaN(val) ? 0 : val;
  const match = String(val).match(/\d+/);
  return match ? parseInt(match[0], 10) : 0;
}

export function resolveUserTierBadgeSync(user, promoPurchases = [], rankLevel = 0) {
  const rawRole = String(user?.role || user?.category || user?.user_type || user?.role_label || user?.role_name || "").toLowerCase();

  // If user has an explicit franchise/agency role, display that role
  if (
    rawRole.includes("agency") ||
    rawRole.includes("coordinator") ||
    rawRole.includes("merchant") ||
    rawRole.includes("captain")
  ) {
    return user?.role_name || user?.role_label || user?.role || user?.category || "Agency";
  }

  // If backend profile already provided a valid computed tier_badge, use it directly
  if (user?.tier_badge && typeof user.tier_badge === "string" && user.tier_badge !== "Prime Agent") {
    return user.tier_badge;
  }

  // 1. Check direct level / rank numbers (robust against strings like "L7", "L10")
  const rawLevel =
    rankLevel ||
    user?.achieved_level ||
    user?.rank_level ||
    user?.current_level ||
    user?.highest_rank ||
    user?.highest_achieved_level ||
    user?.rank ||
    user?.current_rank ||
    0;
  const levelNum = parseNumericLevel(rawLevel);

  let has40k = false;
  let has8k = false;
  let has2k = false;

  const pkgStr = String(
    user?.package ||
    user?.package_name ||
    user?.active_package ||
    user?.prime_package ||
    ""
  ).toLowerCase();
  const pkgAmt = Number(user?.package_amount || user?.package_price || 0);

  if (pkgAmt >= 40000 || pkgStr.includes("40000") || pkgStr.includes("promoter") || pkgStr.includes("stage 3") || pkgStr.includes("stage3")) {
    has40k = true;
  } else if (pkgAmt >= 3000 || pkgAmt >= 8000 || pkgStr.includes("8000") || pkgStr.includes("super") || pkgStr.includes("stage 2") || pkgStr.includes("stage2") || pkgStr.includes("4750") || pkgStr.includes("3250")) {
    has8k = true;
  } else if (pkgAmt >= 2000 || pkgStr.includes("2000") || pkgStr.includes("prime") || pkgStr.includes("starter")) {
    has2k = true;
  }

  // Check promoPurchases list
  if (Array.isArray(promoPurchases)) {
    for (const pp of promoPurchases) {
      const status = String(pp?.status || "").toUpperCase();
      if (status === "APPROVED" || status === "ACTIVE") {
        const pPrice = Number(pp?.package?.price || pp?.price || 0);
        const pName = String(pp?.package?.name || pp?.name || "").toLowerCase();
        if (pPrice >= 40000 || pName.includes("40000") || pName.includes("promoter") || pName.includes("stage 3") || pName.includes("stage3")) {
          has40k = true;
        } else if (pPrice >= 3000 || pPrice >= 8000 || pName.includes("8000") || pName.includes("super") || pName.includes("stage 2") || pName.includes("stage2") || pName.includes("4750") || pName.includes("3250")) {
          has8k = true;
        } else if (pPrice >= 2000 || pName.includes("2000") || pName.includes("prime") || pName.includes("750") || pName.includes("starter")) {
          has2k = true;
        }
      }
    }
  }

  // Check if user is activated
  const isActivated =
    Boolean(user?.is_prime || user?.account_active || user?.first_purchase_activated_at || user?.package_buy_date || has2k || has8k || has40k) ||
    levelNum > 0;

  if (!isActivated) {
    return "Not activated";
  }

  // 100% Strict Layer Hierarchy:
  // Layer 10 -> District Agent Promoter
  if (levelNum >= 10 || (has40k && levelNum >= 10)) {
    return "District Agent Promoter";
  }

  // Layer 7 to Layer 9 -> Super Agent
  if (levelNum >= 7) {
    return "Super Agent";
  }

  // Layer 1 to Layer 6 -> Agent
  if (levelNum >= 1 || isActivated) {
    return "Agent";
  }

  return "Not activated";
}

let _sharedDataPromise = null;
function fetchSharedTierData() {
  if (!_sharedDataPromise) {
    _sharedDataPromise = Promise.allSettled([
      listMyPromoPurchases().catch(() => []),
      getUpgradeEligibility().catch(() => null),
    ]).finally(() => {
      setTimeout(() => {
        _sharedDataPromise = null;
      }, 3000);
    });
  }
  return _sharedDataPromise;
}

export function useUserTierBadge(user) {
  const [badge, setBadge] = useState(() => {
    const directSync = resolveUserTierBadgeSync(user);
    if (directSync && directSync !== "Prime Agent" && directSync !== "Agent") return directSync;
    try {
      const cached = localStorage.getItem("tri_user_tier_badge");
      if (cached && !/L\d+/i.test(cached)) {
        // If cached Super Agent but user is level < 7, purge cache
        const lvl = parseNumericLevel(user?.achieved_level || user?.rank_level || user?.current_rank);
        if (cached === "Super Agent" && lvl > 0 && lvl < 7) {
          localStorage.removeItem("tri_user_tier_badge");
          return "Agent";
        }
        return cached;
      }
    } catch (_) {}
    return directSync;
  });

  // Listen for user updates or storage updates
  useEffect(() => {
    const handleUpdate = (e) => {
      const u = e?.detail;
      if (u) {
        const resolved = resolveUserTierBadgeSync(u);
        if (resolved) {
          setBadge(resolved);
          try {
            localStorage.setItem("tri_user_tier_badge", resolved);
          } catch (_) {}
        }
      }
    };
    window.addEventListener("trikonekt:user-updated", handleUpdate);
    return () => window.removeEventListener("trikonekt:user-updated", handleUpdate);
  }, []);

  useEffect(() => {
    const directSync = resolveUserTierBadgeSync(user);
    if (directSync) {
      setBadge(directSync);
      try {
        localStorage.setItem("tri_user_tier_badge", directSync);
      } catch (_) {}
    }
  }, [user]);

  useEffect(() => {
    let alive = true;
    const directSync = resolveUserTierBadgeSync(user);
    const hasExplicitLevel = Boolean(
      parseNumericLevel(
        user?.achieved_level ||
        user?.rank_level ||
        user?.current_level ||
        user?.highest_rank ||
        user?.rank ||
        user?.current_rank
      ) > 0 ||
      user?.tier_badge ||
      (Number(user?.package_amount || 0) >= 2000)
    );

    // If we have an explicit tier or higher rank, no need to re-fetch
    if (directSync && directSync !== "Prime Agent" && (directSync !== "Agent" || hasExplicitLevel)) {
      setBadge(directSync);
      return;
    }

    (async () => {
      try {
        const [purchasesRes, eligRes] = await fetchSharedTierData();

        if (!alive) return;

        const purchases = purchasesRes.status === "fulfilled" && Array.isArray(purchasesRes.value)
          ? purchasesRes.value
          : (purchasesRes.value?.results || []);
        const rankLevel = eligRes.status === "fulfilled"
          ? Number(eligRes.value?.achieved_level || eligRes.value?.current_level || 0)
          : 0;

        const resolved = resolveUserTierBadgeSync(user, purchases, rankLevel);
        setBadge(resolved);
        try {
          localStorage.setItem("tri_user_tier_badge", resolved);
        } catch (_) {}
      } catch (_) {
        if (!alive) return;
        const resolved = resolveUserTierBadgeSync(user);
        setBadge(resolved);
      }
    })();

    return () => {
      alive = false;
    };
  }, [user]);

  return badge;
}
